import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useCrop } from '../../hooks/useCrop';
import { CROP_HANDLES, CROP_ASPECTS, resizeFrameKeepPicture, movePictureInFrame, hasCrop, constrainCropEdges, cropAspectById, cropRatioLabel } from '../../lib/pictureCrop';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Check, ChevronDown, Crop, Maximize2, Minimize2, Proportions, RotateCcw } from 'lucide-react';

const BAR = 3.5; // handle thickness (screen px)
const LEN = 18; // handle length (screen px)

/**
 * On-canvas crop mode for a picture (Publisher "Crop"): the whole picture is shown ghosted, the frame is outlined and
 * carries eight black crop handles. Rendered inside the page container in page-pixel space (× zoom).
 *
 *  • drag a handle      → that edge / corner of the frame moves, the picture stays on the page (crop)
 *  • drag the picture   → the picture moves inside the frame
 *  • aspect ratio       → Crop ▸ Aspect Ratio (or the banner's Ratio menu) locks the frame to 1:1, 4:3, 16:9 …; while
 *                         free, the frame snaps to those ratios as a handle brings it close (Alt: no snapping;
 *                         Shift: keep the ratio the frame has). A badge by the frame shows the ratio in force.
 *  • Esc / Done / click elsewhere → leave the mode (the reducer drops `cropEditId` when the selection changes)
 *
 * Streaming updates go through rawDispatch (no history noise); one snapshot is pushed when a drag starts.
 */
export const CropEditor = ({ object, zoom, canvasRef }) => {
  const { state, stateRef, dispatch, rawDispatch } = useEditor();
  const objRef = useRef(object);
  objRef.current = object;
  const drag = useRef(null);
  // the ratio applied by the current drag (snapped preset or lock) — drives the badge
  const [dragAspect, setDragAspect] = useState(null);

  const w = Math.max(1, object.width), h = Math.max(1, object.height);
  const cx = (object.x + w / 2) * zoom, cy = (object.y + h / 2) * zoom;
  const rot = object.rotation || 0;
  const p = object.properties || {};
  const crop = hasCrop(p) ? p.crop : { x: 0, y: 0, w, h };
  const locked = cropAspectById(state.cropAspect);

  /** Pointer → local (unrotated, frame-centred) page px. */
  const toLocal = useCallback((e) => {
    const o = objRef.current;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / zoom, py = (e.clientY - rect.top) / zoom;
    const dx = px - (o.x + o.width / 2), dy = py - (o.y + o.height / 2);
    const r = (-(o.rotation || 0) * Math.PI) / 180;
    return { x: dx * Math.cos(r) - dy * Math.sin(r), y: dx * Math.sin(r) + dy * Math.cos(r) };
  }, [canvasRef, zoom]);

  const stream = useCallback((patch) => {
    const o = objRef.current;
    // keepCrop: in crop mode the frame edge moves and the picture stays where it is on the page (the reducer would
    // otherwise scale the picture with the frame, which is what a normal resize does)
    rawDispatch({ type: 'UPDATE_OBJECT', payload: { id: o.id, keepCrop: true, data: { ...(patch.x !== undefined ? { x: patch.x, y: patch.y, width: patch.width, height: patch.height } : {}), properties: { ...o.properties, crop: patch.crop } } } });
  }, [rawDispatch]);

  const startDrag = useCallback((kind, e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch({ type: 'PUSH_HISTORY' });
    const start = { local: toLocal(e), object: objRef.current };
    drag.current = { kind, start };
    const onMove = (ev) => {
      if (!drag.current) return;
      // deltas are measured in the START object's local space (the frame centre moves while cropping, the picture does not)
      const so = drag.current.start.object;
      const rect = canvasRef.current.getBoundingClientRect();
      const px = (ev.clientX - rect.left) / zoom, py = (ev.clientY - rect.top) / zoom;
      const dx0 = px - (so.x + so.width / 2), dy0 = py - (so.y + so.height / 2);
      const r = (-(so.rotation || 0) * Math.PI) / 180;
      const lx = dx0 * Math.cos(r) - dy0 * Math.sin(r), ly = dx0 * Math.sin(r) + dy0 * Math.cos(r);
      const dx = lx - drag.current.start.local.x, dy = ly - drag.current.start.local.y;
      if (kind === 'move') {
        stream({ crop: movePictureInFrame(so, dx, dy) });
        return;
      }
      const hnd = CROP_HANDLES.find((hh) => hh.id === kind);
      const edges = { l: hnd.ex < 0 ? dx : 0, r: hnd.ex > 0 ? dx : 0, t: hnd.ey < 0 ? dy : 0, b: hnd.ey > 0 ? dy : 0 };
      // Crop ▸ Aspect Ratio locks the frame; Shift keeps the ratio the frame had when the drag began; otherwise the frame
      // snaps to a common ratio when it comes close (Alt: drag freely)
      const lock = cropAspectById(stateRef.current.cropAspect).ratio || (ev.shiftKey ? so.width / Math.max(1, so.height) : null);
      const { edges: fitted, aspect } = constrainCropEdges(so, edges, kind, { ratio: lock, snap: !ev.altKey });
      setDragAspect(aspect);
      stream(resizeFrameKeepPicture(so, fitted));
    };
    const stop = () => {
      drag.current = null;
      setDragAspect(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [dispatch, toLocal, stream, canvasRef, zoom, stateRef]);

  useEffect(() => () => { drag.current = null; }, []);

  const W = w * zoom, H = h * zoom;
  const picX = (crop.x - w / 2) * zoom, picY = (crop.y - h / 2) * zoom, picW = crop.w * zoom, picH = crop.h * zoom;
  const flip = `${p.flipX ? 'scaleX(-1)' : ''} ${p.flipY ? 'scaleY(-1)' : ''}`.trim();
  // badge: the ratio in force — a snapped preset while dragging (accent), or the locked preset at rest (dark)
  const badge = dragAspect ? { label: dragAspect.label, kind: locked.ratio ? 'locked' : 'snapped' } : locked.ratio ? { label: locked.label, kind: 'locked' } : null;
  const badgeW = badge ? Math.max(30, badge.label.length * 7 + 12) : 0;

  return (
    <svg className="absolute inset-0" style={{ width: '100%', height: '100%', overflow: 'visible', zIndex: 1000, pointerEvents: 'none' }} data-testid="crop-editor" aria-label="Crop picture" role="group" data-aspect={locked.ratio ? locked.id : 'free'} data-snapped={dragAspect ? dragAspect.id : undefined}>
      <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
        {/* the whole picture, ghosted where it falls outside the frame */}
        <g style={{ opacity: 0.35 }}>
          <image href={p.src} x={picX} y={picY} width={picW} height={picH} preserveAspectRatio="none" style={{ transform: flip || undefined, transformOrigin: `${picX + picW / 2}px ${picY + picH / 2}px` }} data-testid="crop-ghost" />
        </g>
        {/* frame outline */}
        <rect x={-W / 2} y={-H / 2} width={W} height={H} fill="none" stroke="#111" strokeWidth="1" vectorEffect="non-scaling-stroke" data-testid="crop-frame" />
        {/* drag surface: the picture inside the frame moves */}
        <rect x={-W / 2} y={-H / 2} width={W} height={H} fill="transparent" style={{ pointerEvents: 'all', cursor: 'move' }} onPointerDown={(e) => { if (e.button === 0) startDrag('move', e); }} data-testid="crop-move-surface" />
        {/* eight black crop handles: L-shapes at the corners, bars on the edges */}
        {CROP_HANDLES.map((hnd) => {
          const hx = (hnd.ex * W) / 2, hy = (hnd.ey * H) / 2;
          const parts = [];
          if (hnd.ex !== 0 && hnd.ey !== 0) {
            // corner L: a horizontal and a vertical bar meeting at the corner, drawn inward
            parts.push(<rect key="h" x={hnd.ex < 0 ? hx : hx - LEN} y={hnd.ey < 0 ? hy : hy - BAR} width={LEN} height={BAR} />);
            parts.push(<rect key="v" x={hnd.ex < 0 ? hx : hx - BAR} y={hnd.ey < 0 ? hy : hy - LEN} width={BAR} height={LEN} />);
          } else if (hnd.ex === 0) {
            parts.push(<rect key="b" x={hx - LEN / 2} y={hnd.ey < 0 ? hy : hy - BAR} width={LEN} height={BAR} />);
          } else {
            parts.push(<rect key="b" x={hnd.ex < 0 ? hx : hx - BAR} y={hy - LEN / 2} width={BAR} height={LEN} />);
          }
          return (
            <g key={hnd.id} fill="#111" stroke="#fff" strokeWidth="0.75" style={{ pointerEvents: 'all', cursor: hnd.cursor }} onPointerDown={(e) => { if (e.button === 0) startDrag(hnd.id, e); }} data-testid={`crop-handle-${hnd.id}`}>
              {/* generous invisible hit area */}
              <rect x={hx - 10} y={hy - 10} width={20} height={20} fill="transparent" stroke="none" />
              {parts}
            </g>
          );
        })}
        {/* aspect-ratio badge just above the frame's top-right corner */}
        {badge && (
          <g transform={`translate(${W / 2 - badgeW} ${-H / 2 - 24})`} data-testid="crop-aspect-badge" data-kind={badge.kind} aria-label={`Aspect ratio ${badge.label}${badge.kind === 'locked' ? ' (locked)' : ' (snapped)'}`}>
            <rect width={badgeW} height={18} rx={9} fill={badge.kind === 'snapped' ? 'hsl(var(--accent))' : 'hsl(var(--foreground))'} stroke="#fff" strokeWidth="1" />
            <text x={badgeW / 2} y={12.5} textAnchor="middle" fontSize="11" fontWeight="600" fontFamily="Inter, system-ui, sans-serif" fill={badge.kind === 'snapped' ? 'hsl(var(--accent-foreground))' : 'hsl(var(--background))'}>{badge.label}</text>
          </g>
        )}
      </g>
    </svg>
  );
};

/** Crop ▸ Aspect Ratio chooser (banner + ribbon share the list). `aspect` = current preset id. */
export const CropAspectMenuItems = ({ aspect, onPick, testIdPrefix = 'crop-aspect' }) => (
  <DropdownMenuRadioGroup value={aspect || 'free'} onValueChange={onPick}>
    {CROP_ASPECTS.map((a) => (
      <DropdownMenuRadioItem key={a.id} value={a.id} className="text-[13px] pr-3" data-testid={`${testIdPrefix}-${a.id.replace(':', 'x')}`}>
        <span className="inline-flex items-center gap-2">
          <AspectSwatch ratio={a.ratio} />
          {a.name}
        </span>
      </DropdownMenuRadioItem>
    ))}
  </DropdownMenuRadioGroup>
);

/** A tiny outlined rectangle of the ratio (a dashed square for "free"). */
const AspectSwatch = ({ ratio }) => {
  const box = 16;
  const rw = ratio ? (ratio >= 1 ? box : box * ratio) : box, rh = ratio ? (ratio >= 1 ? box / ratio : box) : box;
  return (
    <svg width={box + 2} height={box + 2} viewBox={`0 0 ${box + 2} ${box + 2}`} aria-hidden="true" className="text-muted-foreground shrink-0">
      <rect x={1 + (box - rw) / 2} y={1 + (box - rh) / 2} width={rw} height={rh} fill="none" stroke="currentColor" strokeWidth="1.25" strokeDasharray={ratio ? undefined : '2 2'} rx="1" />
    </svg>
  );
};

/** Floating mode banner (outside the scrolling page so it is never clipped). */
export const CropBanner = ({ object }) => {
  const { exit, fit, fill, clear, aspect, setAspect } = useCrop();
  const cropped = hasCrop(object.properties) && (object.properties.crop.x < -0.5 || object.properties.crop.y < -0.5 || object.properties.crop.x + object.properties.crop.w > object.width + 0.5 || object.properties.crop.y + object.properties.crop.h > object.height + 0.5);
  const current = cropAspectById(aspect);
  const frameLabel = cropRatioLabel(object.width / Math.max(1, object.height));
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-2 z-[60] flex items-center gap-2 rounded-full border border-border bg-popover/90 backdrop-blur-md px-3 py-1 shadow-md text-[12px] whitespace-nowrap max-w-[calc(100%-1rem)]"
      role="status"
      data-testid="crop-banner"
    >
      <Crop className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
      <span className="text-foreground font-medium hidden sm:inline">Cropping</span>
      <span className="text-muted-foreground hidden xl:inline" data-testid="crop-banner-hint">· drag a black handle to crop · drag the picture to move it · Alt: no snapping</span>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} title={current.ratio ? `Aspect ratio ${current.label} is locked for the crop handles — the frame is ${frameLabel}` : `Free — the frame (${frameLabel}) snaps to 1:1, 4:3, 16:9 … as you drag; pick a ratio to lock it`} data-testid="crop-aspect-menu" data-aspect={current.id}>
            <Proportions className="h-3.5 w-3.5 mr-1" aria-hidden="true" /><span className="hidden md:inline">Ratio:&nbsp;</span>{current.ratio ? current.label : 'Free'} <ChevronDown className="h-3 w-3 ml-0.5 opacity-70" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="z-[90] min-w-[200px]" data-testid="crop-aspect-menu-content">
          <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal">Aspect ratio · frame is {frameLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <CropAspectMenuItems aspect={current.id} onPick={(id) => setAspect(id, object.id)} />
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={() => fit(object.id)} title="Fit — show the whole picture inside the frame" aria-label="Fit" data-testid="crop-fit"><Minimize2 className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Fit</span></Button>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={() => fill(object.id)} title="Fill — fill the frame with the picture" aria-label="Fill" data-testid="crop-fill"><Maximize2 className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Fill</span></Button>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={() => clear(object.id)} disabled={!cropped} title="Clear Crop — grow the frame to show the whole picture" aria-label="Clear Crop" data-testid="crop-clear"><RotateCcw className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Clear Crop</span></Button>
      <Button size="sm" className="h-6 px-2 text-[12px]" onMouseDown={(e) => e.preventDefault()} onClick={exit} data-testid="crop-done"><Check className="h-3.5 w-3.5 mr-1" /> Done</Button>
    </div>
  );
};
